import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputDate from "../../../components/InputDate";
import InputSelect from "../../../components/InputSelect";
import InputTextarea from "../../../components/InputTextarea";
import Icon from "../../../components/Icon";
import SelectCompany from "../../../components/selects/SelectCompany";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { getBillingCompanyLabel, useBillingCompanies } from "../../Finance/billingCompany";

const emptyForm = {
    companyId: null,
    billingCompanyId: null,
    number: '',
    series: '',
    accessKey: '',
    issuedAt: '',
    receivedAt: '',
    amount: 'R$ 0,00',
    otherCosts: 'R$ 0,00',
    notes: '',
};

/**
 * Lançamento da nota de entrada (BDM-1050).
 *
 * A ordem da tela é a do menor esforço: soltar o XML resolve tudo, colar a
 * chave resolve quase tudo, e digitar número/valor/emissão é o caminho de
 * quem recebeu a nota só em papel.
 *
 * `purchaseOrder` chega preenchido quando o modal é aberto de dentro de uma
 * compra; na tela de notas o vínculo é escolhido aqui.
 */
export const ModalPurchaseInvoiceForm = ({ show, close, invoice, purchaseOrder = null, onSaved, level }) => {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [reading, setReading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [xmlFile, setXmlFile] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [purchaseOrderId, setPurchaseOrderId] = useState(null);
    const [unknownSupplier, setUnknownSupplier] = useState(null);

    const xmlInputRef = useRef(null);
    const pdfInputRef = useRef(null);

    const { billingCompanies, defaultBillingCompanyId } = useBillingCompanies();

    const isEdit = !!invoice;
    const lockedToPurchaseOrder = !!purchaseOrder;

    useEffect(() => {
        if (!show) return;

        setForm({
            companyId: invoice?.company_id ?? purchaseOrder?.company_id ?? null,
            billingCompanyId: invoice?.billing_company_id ?? defaultBillingCompanyId ?? null,
            number: invoice?.number ?? '',
            series: invoice?.series ?? '',
            accessKey: invoice?.access_key ?? '',
            issuedAt: invoice?.issued_at ? invoice.issued_at.substring(0, 10) : '',
            receivedAt: invoice?.received_at ? invoice.received_at.substring(0, 10) : '',
            amount: convertIntToMoney(invoice?.amount ?? 0, true),
            otherCosts: convertIntToMoney(invoice?.other_costs ?? 0, true),
            notes: invoice?.notes ?? '',
        });

        setFile(null);
        setXmlFile(null);
        setUnknownSupplier(null);
        setSuggestions([]);
        setPurchaseOrderId(
            purchaseOrder?.id
            ?? invoice?.purchase_orders?.[0]?.id
            ?? null
        );
    }, [show, invoice, purchaseOrder, defaultBillingCompanyId]);

    // Na tela geral, escolher o fornecedor já traz as compras em aberto dele:
    // vincular passa a ser um clique, não uma busca.
    useEffect(() => {
        if (!show || lockedToPurchaseOrder || !form.companyId) {
            return;
        }

        api.get(`/purchase-invoice/suggestions`, {
            params: { company_id: form.companyId, amount: convertMoenyToInt(form.amount) || 0 },
        }).then(res => setSuggestions(res.data.resources ?? [])).catch(() => setSuggestions([]));
    }, [show, form.companyId, lockedToPurchaseOrder]);

    const update = changes => setForm(current => ({ ...current, ...changes }));

    const readXml = selected => {
        if (!selected) return;

        setReading(true);
        setXmlFile(selected);

        const formData = new FormData();
        formData.append('xml_file', selected);

        api.post(`/purchase-invoice/parse-xml`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(res => {
            const data = res.data.resource;

            update({
                companyId: data.company_id ?? form.companyId,
                billingCompanyId: data.billing_company_id ?? form.billingCompanyId,
                number: data.number ?? '',
                series: data.series ?? '',
                accessKey: data.access_key ?? '',
                issuedAt: data.issued_at ?? '',
                amount: convertIntToMoney(data.amount ?? 0, true),
                otherCosts: convertIntToMoney(data.other_costs ?? 0, true),
            });

            setUnknownSupplier(data.company_matched ? null : {
                name: data.company_trade_name,
                cnpj: data.company_cnpj,
            });

            if (!lockedToPurchaseOrder) {
                const options = res.data.purchase_orders ?? [];
                setSuggestions(options);
                setPurchaseOrderId(current => current ?? options[0]?.id ?? null);
            }

            toast.success('Li o XML e preenchi a nota');
        }).catch(renderError).then(() => setReading(false));
    };

    const onDrop = event => {
        event.preventDefault();
        setDragging(false);

        readXml(event.dataTransfer.files?.[0]);
    };

    const save = () => {
        const amount = convertMoenyToInt(form.amount) || 0;

        if (!form.number.trim()) {
            toast.error('Informe o número da nota');
            return;
        }

        if (!form.issuedAt) {
            toast.error('Informe a data de emissão da nota');
            return;
        }

        setSaving(true);

        const formData = new FormData();
        formData.append('number', form.number.trim());
        formData.append('series', form.series ?? '');
        formData.append('access_key', form.accessKey ?? '');
        formData.append('issued_at', form.issuedAt);
        formData.append('received_at', form.receivedAt ?? '');
        formData.append('amount', amount);
        formData.append('other_costs', convertMoenyToInt(form.otherCosts) || 0);
        formData.append('notes', form.notes ?? '');
        formData.append('company_id', form.companyId ?? '');
        formData.append('billing_company_id', form.billingCompanyId ?? '');

        if (purchaseOrderId) {
            formData.append('purchase_orders[0][id]', purchaseOrderId);
        }

        if (file) formData.append('file', file);
        if (xmlFile) formData.append('xml_file', xmlFile);

        const url = isEdit ? `/purchase-invoice/${invoice.id}` : `/purchase-invoice`;

        // POST também na edição: multipart não sobe em PUT.
        api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(() => {
                toast.success(isEdit ? 'Nota atualizada' : 'Nota de entrada lançada');
                close();
                onSaved();
            })
            .catch(renderError)
            .then(() => setSaving(false));
    };

    const purchaseOrderOptions = [
        // A nota pode chegar antes da compra existir no sistema: ficar sem
        // vínculo é situação prevista, não erro.
        { label: 'Sem compra vinculada', value: null },
        ...suggestions.map(option => ({
            label: `#${option.code} — ${convertIntToMoney(option.total, true)}`
                + (option.invoiced_amount > 0 ? ` (${convertIntToMoney(option.invoiced_amount, true)} em notas)` : ''),
            value: option.id,
        })),
    ];

    return (
        <Modal
            level={level}
            className={`purchase-invoice-form-modal`}
            size={`big`}
            title={isEdit ? `Nota de entrada ${invoice.number}` : `Lançar nota de entrada`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    <Button type={`primary`} size={`small`} text={`Salvar`} action={save} loading={saving} />
                </>
            }
        >
            <div className="section purchase-invoice-form">
                <div
                    className={`invoice-dropzone ${dragging ? 'dragging' : ''} ${reading ? 'reading' : ''}`}
                    onDragOver={event => { event.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => xmlInputRef.current?.click()}
                >
                    <input
                        ref={xmlInputRef}
                        type="file"
                        accept=".xml,text/xml,application/xml"
                        style={{ display: 'none' }}
                        onChange={event => { readXml(event.target.files?.[0]); event.target.value = ''; }}
                    />

                    <Icon name={reading ? `loading` : `upload`} className={reading ? 'spin' : ''} />
                    <span>
                        <strong>
                            {reading
                                ? 'Lendo o XML...'
                                : xmlFile
                                    ? xmlFile.name
                                    : 'Arraste aqui o XML da nota'
                            }
                        </strong>
                        <small>
                            {xmlFile && !reading
                                ? 'O XML fica guardado junto da nota.'
                                : 'Preenche número, série, chave, emissão e valor sozinho. Ou preencha à mão abaixo.'
                            }
                        </small>
                    </span>
                </div>

                {unknownSupplier &&
                    <p className={`fiscal-hint warning`}>
                        O emitente <strong>{unknownSupplier.name}</strong>
                        {unknownSupplier.cnpj ? ` (${unknownSupplier.cnpj})` : ''} não está cadastrado
                        como empresa. Escolha o fornecedor abaixo ou cadastre a empresa antes.
                    </p>
                }

                <div className="row">
                    <div className="col-6">
                        {lockedToPurchaseOrder
                            ? <Input label={`Fornecedor`} value={purchaseOrder.company_trade_name ?? '—'} change={() => {}} disabled={true} />
                            : <SelectCompany
                                label={`Fornecedor`}
                                nullable={true}
                                selectedCompany={form.companyId}
                                setSelectedCompany={value => update({ companyId: value })}
                                create={{ defaultType: ['product'], level: (level ?? 0) + 1 }}
                            />
                        }
                    </div>
                    <div className="col-3">
                        <Input label={`Número da nota`} value={form.number} change={value => update({ number: value })} />
                    </div>
                    <div className="col-3">
                        <Input label={`Série`} value={form.series} change={value => update({ series: value })} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-3">
                        <Input label={`Valor da nota`} value={form.amount} change={value => update({ amount: value })} mask={`convertToMoney`} />
                    </div>
                    <div className="col-3">
                        <Input label={`Frete, IPI e outros`} value={form.otherCosts} change={value => update({ otherCosts: value })} mask={`convertToMoney`} />
                    </div>
                    <div className="col-3">
                        <InputDate label={`Emissão`} format={`YYYY-MM-DD`} value={form.issuedAt} change={value => update({ issuedAt: value })} />
                    </div>
                    <div className="col-3">
                        <InputDate label={`Recebida em`} format={`YYYY-MM-DD`} value={form.receivedAt} change={value => update({ receivedAt: value })} />
                    </div>
                </div>

                <div className="row">
                    {!lockedToPurchaseOrder &&
                        <div className="col-6">
                            <InputSelect
                                label={`Pedido de compra`}
                                options={purchaseOrderOptions}
                                value={purchaseOrderId}
                                change={setPurchaseOrderId}
                            />
                        </div>
                    }
                    {billingCompanies.length > 1 &&
                        <div className="col-6">
                            <InputSelect
                                label={`Empresa que recebeu a nota`}
                                options={billingCompanies.map(company => ({
                                    label: getBillingCompanyLabel(company),
                                    value: company.id,
                                }))}
                                value={form.billingCompanyId}
                                change={value => update({ billingCompanyId: value })}
                            />
                        </div>
                    }
                </div>

                {lockedToPurchaseOrder &&
                    <p className={`fiscal-hint`}>
                        Esta nota entra no pedido de compra <strong>#{purchaseOrder.code}</strong>
                        {' '}({convertIntToMoney(purchaseOrder.total, true)}).
                        {purchaseOrder.invoiced_amount > 0 &&
                            ` Já lançadas: ${convertIntToMoney(purchaseOrder.invoiced_amount, true)}.`
                        }
                    </p>
                }

                <div className="row">
                    <div className="col-6">
                        <Input label={`Chave de acesso`} value={form.accessKey} change={value => update({ accessKey: value.replace(/\D/g, '').substring(0, 44) })} />
                    </div>
                    <div className="col-6">
                        <div className={`invoice-pdf-field`}>
                            <label>Anexo (PDF da nota)</label>
                            <input
                                ref={pdfInputRef}
                                type="file"
                                accept="application/pdf"
                                style={{ display: 'none' }}
                                onChange={event => { setFile(event.target.files?.[0] ?? null); event.target.value = ''; }}
                            />
                            <button onClick={() => pdfInputRef.current?.click()}>
                                <Icon name={`upload-small`} size={16} />
                                {file ? file.name : (invoice?.file_url ? 'Trocar o PDF anexado' : 'Escolher PDF')}
                            </button>
                        </div>
                    </div>
                </div>

                <InputTextarea label={`Observações`} value={form.notes} change={value => update({ notes: value })} />

                {isEdit && invoice?.issued_at &&
                    <p className={`fiscal-hint`}>
                        Lançada em {moment(invoice.created_at).format('DD/MM/YYYY')}.
                    </p>
                }
            </div>
        </Modal>
    );
};

export default ModalPurchaseInvoiceForm;
